import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type CountryTaxRate = components["schemas"]["CountryTaxRateAdminResponse"];

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function CountryTaxRatesTable() {
  const [rates, setRates] = useState<CountryTaxRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [country, setCountry] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function fetchRates() {
      setLoading(true);
      setError(null);
      const trimmed = country.trim();
      const { data, error: apiError } = await api.GET("/admin/country-tax-rates", {
        params: { query: trimmed ? { country: trimmed } : {} },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load country tax rates.");
      } else {
        setRates((data as CountryTaxRate[] | undefined) ?? []);
      }
      setLoading(false);
    }
    const timer = setTimeout(fetchRates, country ? 250 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [country]);

  // Group by country for the UI: ISO code → ordered rates (current first, then superseded).
  const grouped = useMemo(() => {
    const byCountry = new Map<string, CountryTaxRate[]>();
    for (const r of rates) {
      const key = r.countryCode;
      if (!byCountry.has(key)) byCountry.set(key, []);
      byCountry.get(key)!.push(r);
    }
    return [...byCountry.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rates]);

  return (
    <div>
      <div className="mb-4 flex items-end gap-4">
        <div className="space-y-2">
          <Label htmlFor="country-filter">Filter by country</Label>
          <Input
            id="country-filter"
            placeholder="GB"
            value={country}
            onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))}
            className="w-32 font-mono"
          />
        </div>
        <div className="flex items-center gap-3">
          {loading && <Spinner className="size-5" />}
          {!loading && (
            <span className="text-sm text-muted-foreground">
              {rates.length} {rates.length === 1 ? "rate" : "rates"} ·{" "}
              {grouped.length} {grouped.length === 1 ? "country" : "countries"}
            </span>
          )}
        </div>
      </div>

      {error && <p className="text-destructive mb-4 text-sm">{error}</p>}

      {grouped.length === 0 && !loading ? (
        <div className="text-muted-foreground rounded-md border py-12 text-center text-sm">
          No country tax rates {country ? `for "${country}"` : "configured"}.
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([countryCode, rows]) => (
            <div key={countryCode}>
              <div className="mb-2 flex items-center gap-3">
                <Badge variant="secondary" className="font-mono">
                  {countryCode}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {rows.length} {rows.length === 1 ? "rate" : "rates"}
                </span>
              </div>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[160px]">Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead>Effective from</TableHead>
                      <TableHead>Effective to</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => {
                      const inForce = !r.effectiveTo || new Date(r.effectiveTo) > new Date();
                      return (
                        <TableRow key={r.id}>
                          <TableCell className="font-mono font-medium">{r.code}</TableCell>
                          <TableCell>{r.name}</TableCell>
                          <TableCell className="text-right">{r.rate}%</TableCell>
                          <TableCell>{formatDate(r.effectiveFrom)}</TableCell>
                          <TableCell>{formatDate(r.effectiveTo)}</TableCell>
                          <TableCell>
                            <Badge variant={inForce ? "default" : "outline"}>
                              {inForce ? "In force" : "Superseded"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
